import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Activity,
  ArrowLeft,
  Bot,
  Check,
  CheckSquare,
  ChevronDown,
  ChevronsUpDown,
  ClipboardList,
  Database,
  FileText,
  Gauge,
  GitBranch,
  Layers,
  Loader2,
  LogOut,
  Lightbulb,
  Megaphone,
  Menu,
  Moon,
  Plus,
  Route,
  Settings,
  Settings2,
  Sparkles,
  Sun,
  UserMinus,
  Video,
  X,
  Image,
} from 'lucide-react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';

import { LayoutChromeContext, isScrollContainedRoute, isWorkspaceRoute } from './layoutChrome';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { Button } from '../components/ui/button';
import { api } from '../lib/api';
import { syncAuthAcrossTabs, useAuthStore } from '../stores/auth';
import { useBrandingStore } from '../stores/branding';
import { useProjectStore } from '../stores/project';
import { useThemeStore } from '../stores/theme';
import { BodhiniLogo } from '../components/BodhiniLogo';
import { AddProjectMemberModal } from '../components/projects/AddProjectMemberModal';
import { UserMenu } from '../components/UserMenu';
import { GUIDE_TYPE_SECTIONS } from '../lib/guideRoutes';

// ── Theme toggle ───────────────────────────────────────────────────────────

function ThemeToggle() {
  const { theme, setTheme } = useThemeStore();
  const isDark = theme === 'dark';
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      onClick={() => setTheme(isDark ? 'light' : 'dark')}
      aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
    >
      {isDark ? <Sun size={15} /> : <Moon size={15} />}
    </Button>
  );
}

// ── Project switcher ───────────────────────────────────────────────────────

interface ProjectSummary {
  id: string;
  name: string;
}

interface ProjectMemberItem {
  user_id: string;
  email: string;
  display_name: string | null;
  role: 'owner' | 'member';
}

interface ProjectDetail extends ProjectSummary {
  owner_id: string;
  owner_email: string | null;
  owner_display_name: string | null;
  member_count: number;
  members: ProjectMemberItem[];
}

function ProjectEditPanel({
  projectId,
  currentUserId,
  onBack,
  onNavigate,
}: {
  projectId: string;
  currentUserId: string | null;
  onBack: () => void;
  onNavigate: (path: string) => void;
}) {
  const qc = useQueryClient();
  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState('');
  const [addMemberOpen, setAddMemberOpen] = useState(false);
  const tenantId = useAuthStore((s) => s.tenantId) ?? '';

  const { data: project, isLoading } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => api.get<ProjectDetail>(`/projects/${projectId}`),
  });

  const isOwner = project?.owner_id === currentUserId;

  const rename = useMutation({
    mutationFn: () => api.patch(`/projects/${projectId}`, { name: nameInput.trim() }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['projects'] });
      void qc.invalidateQueries({ queryKey: ['project', projectId] });
      toast.success('Project renamed');
      setEditingName(false);
    },
    onError: () => toast.error('Failed to rename project'),
  });

  const removeMember = useMutation({
    mutationFn: (userId: string) =>
      api.delete(`/projects/${projectId}/members/${userId}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['project', projectId] });
      toast.success('Member removed');
    },
    onError: () => toast.error('Failed to remove member'),
  });

  if (isLoading || !project) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 size={16} className="animate-spin text-fg-3" />
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      {/* Header */}
      <div className="flex items-center gap-2 border-b border-surface-3 px-3 py-2.5">
        <button
          type="button"
          onClick={onBack}
          className="flex h-5 w-5 shrink-0 items-center justify-center rounded text-fg-3 hover:text-fg transition-colors"
        >
          <ArrowLeft size={13} />
        </button>
        {editingName ? (
          <form
            className="flex flex-1 items-center gap-1.5"
            onSubmit={(e) => {
              e.preventDefault();
              if (nameInput.trim()) rename.mutate();
            }}
          >
            <input
              autoFocus
              value={nameInput}
              onChange={(e) => setNameInput(e.target.value)}
              className="flex-1 rounded border border-brand-500/40 bg-surface-2 px-2 py-0.5
                         text-[12.5px] text-fg outline-none focus:border-brand-500"
            />
            <button
              type="submit"
              disabled={!nameInput.trim() || rename.isPending}
              className="text-[11.5px] font-semibold text-brand-500 hover:text-brand-600 disabled:opacity-50"
            >
              {rename.isPending ? '…' : 'Save'}
            </button>
            <button
              type="button"
              onClick={() => setEditingName(false)}
              className="text-[11.5px] text-fg-3 hover:text-fg"
            >
              Cancel
            </button>
          </form>
        ) : (
          <>
            <span className="flex-1 truncate text-[13px] font-semibold text-fg">
              {project.name}
            </span>
            {isOwner && (
              <button
                type="button"
                onClick={() => { setNameInput(project.name); setEditingName(true); }}
                className="shrink-0 text-[11px] text-fg-3 hover:text-fg transition-colors"
              >
                Rename
              </button>
            )}
          </>
        )}
      </div>

      {/* Members */}
      <div className="px-3 py-2.5 space-y-1.5">
        <div className="flex items-center justify-between mb-0.5">
          <span className="text-[10.5px] font-semibold uppercase tracking-wider text-fg-3">
            Members
          </span>
          {isOwner && (
            <button
              type="button"
              onClick={() => setAddMemberOpen(true)}
              className="flex items-center gap-1 text-[11px] text-brand-500 hover:text-brand-600 transition-colors"
            >
              <Plus size={10} />
              Add
            </button>
          )}
        </div>

        <AddProjectMemberModal
          open={addMemberOpen}
          onClose={() => setAddMemberOpen(false)}
          projectId={projectId}
          tenantId={tenantId}
        />

        <p className="pt-1 text-[10px] font-semibold uppercase tracking-wider text-fg-3">
          Owner
        </p>
        {[
          {
            key: project.owner_id,
            name: project.owner_display_name?.trim() || project.owner_email || 'Owner',
            isSelf: isOwner,
          },
          ...project.members
            .filter((m) => m.role === 'owner')
            .map((m) => ({
              key: m.user_id,
              name: m.display_name?.trim() || m.email,
              isSelf: m.user_id === currentUserId,
            })),
        ].map((owner) => (
          <div key={owner.key} className="flex items-center gap-2 py-1">
            <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full
                             bg-amber-500/15 text-[9px] font-bold text-amber-600">
              {owner.name.charAt(0).toUpperCase()}
            </span>
            <span className="flex-1 text-[12px] text-fg truncate">
              {owner.name}
              {owner.isSelf && <span className="ml-1 text-[10px] text-fg-3">(you)</span>}
            </span>
            <span className="shrink-0 rounded-full bg-amber-500/12 px-1.5 py-0.5
                             text-[9.5px] font-semibold text-amber-600">
              owner
            </span>
          </div>
        ))}

        <p className="pt-2 text-[10px] font-semibold uppercase tracking-wider text-fg-3">
          Members
        </p>
        {project.members.filter((m) => m.role !== 'owner').map((m) => (
          <div key={m.user_id} className="group/m flex items-center gap-2 py-1">
            <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full
                             bg-brand-500/10 text-[9px] font-bold text-brand-600">
              {(m.display_name ?? m.email).charAt(0).toUpperCase()}
            </span>
            <span className="flex-1 text-[12px] text-fg truncate">
              {m.display_name ?? m.email}
              {m.user_id === currentUserId && (
                <span className="ml-1 text-[10px] text-fg-3">(you)</span>
              )}
            </span>
            <span className="shrink-0 rounded-full bg-surface-3 px-1.5 py-0.5
                             text-[9.5px] font-semibold text-fg-3">
              member
            </span>
            {isOwner && (
              <button
                type="button"
                onClick={() => removeMember.mutate(m.user_id)}
                disabled={removeMember.isPending}
                className="hidden group-hover/m:flex h-5 w-5 items-center justify-center
                           rounded text-fg-3 hover:text-red-400 transition-colors"
                title="Remove member"
              >
                <UserMinus size={11} />
              </button>
            )}
          </div>
        ))}

        {project.members.filter((m) => m.role !== 'owner').length === 0 && (
          <p className="py-1 text-[11.5px] text-fg-3">No additional members.</p>
        )}
      </div>

      {/* Footer */}
      <div className="border-t border-surface-3 px-3 py-2">
        <button
          type="button"
          onClick={() => onNavigate(`/projects/${projectId}/settings`)}
          className="flex items-center gap-1.5 text-[12px] text-fg-3 hover:text-fg transition-colors"
        >
          <Settings2 size={11} />
          Full settings
        </button>
      </div>
    </div>
  );
}

function ProjectSwitcher() {
  const [open, setOpen] = useState(false);
  const [editingProjectId, setEditingProjectId] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const activeProjectId = useProjectStore((s) => s.activeProjectId);
  const setActiveProject = useProjectStore((s) => s.setActiveProject);
  const currentUserId = useAuthStore((s) => s.userId);
  const isAccountAdmin = useAuthStore((s) => s.role) === 'admin';

  const { data: projects = [] } = useQuery({
    queryKey: ['projects'],
    queryFn: () => api.get<ProjectSummary[]>('/projects'),
  });

  const active = projects.find((p) => p.id === activeProjectId);

  // Auto-select the first project when none is active
  useEffect(() => {
    const first = projects[0];
    if (!activeProjectId && first) {
      setActiveProject(first.id);
      void qc.invalidateQueries();
    }
  }, [activeProjectId, projects, setActiveProject, qc]);

  useEffect(() => {
    const handle = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setEditingProjectId(null);
      }
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  const select = (id: string) => {
    setActiveProject(id);
    void qc.invalidateQueries();
    setOpen(false);
    setEditingProjectId(null);
  };

  const openEdit = (e: React.MouseEvent, projectId: string) => {
    e.stopPropagation();
    setEditingProjectId(projectId);
    setOpen(true);
  };

  return (
    <div className="relative px-3 pb-3" ref={ref}>
      <button
        type="button"
        onClick={() => { setOpen((v) => !v); if (open) setEditingProjectId(null); }}
        className="flex w-full items-center gap-2 rounded-xl border border-surface-3
                   bg-surface-1 px-3 py-2 hover:bg-surface-3/50 transition-colors"
      >
        <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-md
                         bg-brand-500/15 text-[10px] font-bold text-brand-600">
          {active?.name.charAt(0).toUpperCase() ?? '…'}
        </span>
        <span className="flex-1 text-left text-[12.5px] font-medium text-fg truncate">
          {active?.name ?? '…'}
        </span>
        <ChevronsUpDown size={12} className="shrink-0 text-fg-3" />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: -4 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: -4 }}
            transition={{ duration: 0.12, ease: [0.16, 1, 0.3, 1] }}
            className="absolute left-3 right-3 top-full z-50 mt-1 overflow-hidden
                       rounded-xl border border-surface-3 bg-surface-1 shadow-xl shadow-black/20"
          >
            {editingProjectId ? (
              <ProjectEditPanel
                projectId={editingProjectId}
                currentUserId={currentUserId}
                onBack={() => setEditingProjectId(null)}
                onNavigate={(path) => { setOpen(false); setEditingProjectId(null); navigate(path); }}
              />
            ) : (
              <>
                {/* Project list */}
                {projects.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => select(p.id)}
                    className="flex w-full items-center gap-2.5 px-3 py-2
                               hover:bg-surface-3/60 transition-colors"
                  >
                    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-md
                                     bg-brand-500/15 text-[10px] font-bold text-brand-600">
                      {p.name.charAt(0).toUpperCase()}
                    </span>
                    <span className="flex-1 text-left text-[12.5px] font-medium text-fg truncate">
                      {p.name}
                    </span>
                    {activeProjectId === p.id && <Check size={12} className="text-brand-500" />}
                  </button>
                ))}

                <div className="mx-2 h-px bg-surface-3" />

                <button
                  type="button"
                  onClick={() => { setOpen(false); navigate('/settings/projects'); }}
                  className="flex w-full items-center gap-2.5 px-3 py-2
                             hover:bg-surface-3/60 transition-colors"
                >
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-md
                                   bg-surface-3 text-fg-3">
                    {isAccountAdmin ? <Plus size={11} /> : <Settings2 size={11} />}
                  </span>
                  <span className="text-[12.5px] font-medium text-fg-2">
                    {isAccountAdmin ? 'New project' : 'My projects'}
                  </span>
                </button>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ── Nav groups ─────────────────────────────────────────────────────────────

const NAV_GROUPS: Array<{
  label: string;
  items: Array<{ to: string; label: string; title?: string; icon: React.ReactNode; badge?: string }>;
}> = [
  {
    label: 'Explain',
    items: [
      { to: '/workflows', label: 'Workflows', title: 'Screenshot step-by-step docs', icon: <Route size={16} aria-hidden /> },
      { to: '/collections', label: 'Collections', title: 'Grouped workflows', icon: <Layers   size={16} aria-hidden /> },
    ],
  },
  {
    label: 'Engage',
    items: [
      ...GUIDE_TYPE_SECTIONS.map(({ listPath, sidebarLabel, type }) => ({
        to: listPath,
        label: sidebarLabel,
        title: sidebarLabel,
        icon:
          type === 'walkthrough' ? <Route size={16} aria-hidden /> :
          type === 'hint' ? <Lightbulb size={16} aria-hidden /> :
          type === 'announcement' ? <Megaphone size={16} aria-hidden /> :
          type === 'checklist' ? <CheckSquare size={16} aria-hidden /> :
          type === 'choice' ? <GitBranch size={16} aria-hidden /> :
          type === 'survey' ? <ClipboardList size={16} aria-hidden /> :
          <ClipboardList size={16} aria-hidden />,
      })),
      { to: '/flow', label: 'Guide flows', title: 'Connected guide sequences', icon: <Route size={16} aria-hidden /> },
    ],
  },
  {
    label: 'Educate',
    items: [
      { to: '/recordings',      label: 'Recordings', title: 'Video screen recordings', icon: <Video     size={16} aria-hidden /> },
      { to: '/media',           label: 'Media',      title: 'Reusable images, audio and video', icon: <Image     size={16} aria-hidden /> },
      { to: '/knowledge-bases', label: 'Knowledge',  title: 'Knowledge bases for AI training', icon: <Database  size={16} aria-hidden /> },
      { to: '/agents',          label: 'AI agents',  title: 'Chat agents powered by your content', icon: <Bot       size={16} aria-hidden /> },
      { to: '/avatars',         label: 'Avatars',    title: 'AI avatars (preview)', icon: <Sparkles  size={16} aria-hidden />, badge: 'Preview' },
    ],
  },
  {
    label: 'Insite',
    items: [
      { to: '/overview',      label: 'Overview',  icon: <Gauge    size={16} aria-hidden /> },
      { to: '/analytics',     label: 'Analytics', icon: <Activity size={16} aria-hidden /> },
      { to: '/docs/overview', label: 'Docs',      icon: <FileText size={16} aria-hidden /> },
    ],
  },
];

// ── Sidebar content (shared between desktop and mobile drawer) ────────────

function SidebarContent({
  email,
  isAdmin,
  onNav,
  onLogout,
}: {
  email: string | null;
  isAdmin: boolean;
  onNav?: () => void;
  onLogout: () => void;
}) {
  const _email = email ?? '';
  const activeProjectId = useProjectStore((s) => s.activeProjectId);
  return (
    <div className="flex h-full flex-col">
      {/* Logo — fixed app identity, never driven by account branding */}
      <div className="flex shrink-0 items-center gap-2.5 px-5 pb-3 pt-5">
        <Link to="/" onClick={onNav} className="flex items-center gap-2.5 min-w-0">
          <BodhiniLogo gradientId="tmFlameSidebar" />
        </Link>
      </div>

      {/* Project switcher */}
      <ProjectSwitcher />

      <nav className="flex flex-col gap-4 overflow-y-auto flex-1 px-3 py-2 scrollbar-thin">
        {NAV_GROUPS.map(({ label, items }) => (
          <div key={label}>
            <p className="px-[11px] pb-1 text-[10.5px] font-semibold uppercase tracking-widest text-fg-3 select-none">
              {label}
            </p>
            <div className="flex flex-col gap-0.5">
              {items.map(({ to, label: itemLabel, title, icon, badge }) => (
                <NavLink
                  key={to}
                  to={to}
                  onClick={onNav}
                  title={title ?? itemLabel}
                  className={({ isActive }) =>
                    `flex items-center gap-[11px] rounded-[11px] px-[11px] py-[9px]
                     text-[13.5px] transition-colors duration-150 ${
                      isActive
                        ? 'bg-brand-500/[0.12] text-brand-600 font-semibold'
                        : 'text-fg-2 font-medium hover:bg-surface-3/50 hover:text-fg'
                    }`
                  }
                >
                  {icon}
                  <span className="flex-1 truncate">{itemLabel}</span>
                  {badge && (
                    <span className="rounded-full bg-amber-500/15 px-1.5 py-0.5
                                     text-[9.5px] font-semibold text-amber-600 leading-none">
                      {badge}
                    </span>
                  )}
                </NavLink>
              ))}
            </div>
          </div>
        ))}

        {/* Settings group — visible when a project is active */}
        {activeProjectId && (
          <div>
            <p className="px-[11px] pb-1 text-[10.5px] font-semibold uppercase tracking-widest text-fg-3 select-none">
              Settings
            </p>
            <div className="flex flex-col gap-0.5">
              <NavLink
                to={`/projects/${activeProjectId}/settings`}
                onClick={onNav}
                title="Project settings and configuration"
                className={({ isActive }) =>
                  `flex items-center gap-[11px] rounded-[11px] px-[11px] py-[9px]
                   text-[13.5px] transition-colors duration-150 ${
                    isActive
                      ? 'bg-brand-500/[0.12] text-brand-600 font-semibold'
                      : 'text-fg-2 font-medium hover:bg-surface-3/50 hover:text-fg'
                  }`
                }
              >
                <Settings2 size={16} aria-hidden />
                <span className="truncate">Project settings</span>
              </NavLink>
            </div>
          </div>
        )}
      </nav>

      {/* Appearance toggle + user — bottom */}
      <div className="shrink-0 border-t border-surface-3 px-3 py-3 space-y-1">
        {_email ? (
          <UserMenu email={_email} isAdmin={isAdmin} onLogout={onLogout} />
        ) : (
          <Link
            to="/login"
            onClick={onNav}
            className="flex items-center gap-2 rounded-[11px] px-3 py-2
                       text-[13px] font-medium text-fg-3 hover:bg-surface-3/50
                       hover:text-fg transition-colors"
          >
            Sign in
          </Link>
        )}
      </div>
    </div>
  );
}

export function AppLayout() {
  const navigate  = useNavigate();
  const location  = useLocation();
  const email     = useAuthStore((s) => s.email);
  const token     = useAuthStore((s) => s.token);
  const tenantId  = useAuthStore((s) => s.tenantId);
  const role      = useAuthStore((s) => s.role);
  const setRole   = useAuthStore((s) => s.setRole);
  const clearAuth = useAuthStore((s) => s.clearAuth);
  const isAdmin   = role === 'admin';
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const mainRef   = useRef<HTMLElement>(null);
  const workspace = isWorkspaceRoute(location.pathname);
  const scrollContained = isScrollContainedRoute(location.pathname);
  const chrome    = useMemo(() => ({ workspace, openNav: () => setMobileNavOpen(true) }), [workspace]);

  // Close drawer on route change; reset scroll and tab title
  useEffect(() => {
    setMobileNavOpen(false);
    document.title = 'Bodhini';
    mainRef.current?.scrollTo({ top: 0 });
  }, [location.pathname]);

  useEffect(() => syncAuthAcrossTabs(), []);

  // Fetch role for the active account
  useEffect(() => {
    if (!token || !tenantId) return;
    api.get<Array<{ account_id: string; account_name: string; role: string }>>('/auth/accounts')
      .then((accounts) => {
        const current = accounts.find((a) => a.account_id === tenantId);
        setRole(current?.role ?? null);
      })
      .catch(() => { /* best-effort — role stays as previously cached */ });
  }, [token, tenantId, setRole]);

  // Fetch branding once — populates store for use in exports/guides only, not the app shell
  const setBranding = useBrandingStore((s) => s.setBranding);
  useEffect(() => {
    api.get<{
      company_name: string; tagline: string | null;
      palette_key: string; logo_url: string | null;
    }>('/branding').then((b) => {
      setBranding({ paletteKey: b.palette_key, companyName: b.company_name, tagline: b.tagline ?? '', logoUrl: b.logo_url ?? null });
    }).catch(() => { /* best-effort */ });
  }, [setBranding]);

  const handleLogout = () => { clearAuth(); navigate('/login'); };

  return (
    <LayoutChromeContext.Provider value={chrome}>
    <div className="flex h-full min-h-0 w-full flex-1 overflow-hidden bg-surface">

      {/* ── Desktop sidebar (hidden on mobile and in workspace mode) ── */}
      {!workspace && (
        <aside className="hidden md:flex w-[212px] shrink-0 flex-col border-r border-surface-3 bg-surface-2">
          <SidebarContent
            email={email}
            isAdmin={isAdmin}
            onLogout={handleLogout}
          />
        </aside>
      )}

      {/* ── Sidebar drawer (mobile always; desktop in workspace mode) ── */}
      <AnimatePresence>
        {mobileNavOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              key="backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className={`fixed inset-0 z-40 bg-black/50 backdrop-blur-sm ${workspace ? '' : 'md:hidden'}`}
              onClick={() => setMobileNavOpen(false)}
            />
            {/* Drawer */}
            <motion.div
              key="drawer"
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
              className={`fixed inset-y-0 left-0 z-50 flex w-[240px] flex-col
                         border-r border-surface-3 bg-surface-2 ${workspace ? '' : 'md:hidden'}`}
            >
              {/* Close button */}
              <button
                type="button"
                onClick={() => setMobileNavOpen(false)}
                className="absolute right-3 top-3.5 flex h-7 w-7 items-center justify-center
                           rounded-lg text-fg-3 hover:bg-surface-2 hover:text-fg transition-colors"
                aria-label="Close menu"
              >
                <X size={15} />
              </button>
              <SidebarContent
                email={email}
                isAdmin={isAdmin}
                onNav={() => setMobileNavOpen(false)}
                onLogout={handleLogout}
              />
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* ── Main scrollable content ─────────────────────────────── */}
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">

        {/* Mobile top bar (workspace pages render their own header with a nav trigger) */}
        {!workspace && (
        <header className="flex h-14 shrink-0 items-center gap-3 border-b border-surface-3
                           bg-surface-2 px-4 md:hidden">
          <button
            type="button"
            onClick={() => setMobileNavOpen(true)}
            className="flex h-8 w-8 items-center justify-center rounded-lg
                       text-fg-3 hover:bg-surface-3/50 hover:text-fg transition-colors"
            aria-label="Open menu"
          >
            <Menu size={18} />
          </button>
          <Link to="/" className="flex min-w-0 flex-1 items-center">
            <BodhiniLogo
              markSize={20}
              gradientId="tmFlameMobile"
              wordmarkClassName="truncate text-[17px] font-semibold text-fg"
            />
          </Link>
        </header>
        )}

        <main ref={mainRef} className={`flex min-w-0 flex-1 min-h-0 ${workspace || scrollContained ? 'overflow-hidden' : 'overflow-y-auto overflow-x-hidden'}`}>
          <div className={`min-w-0 flex-1 min-h-0 ${workspace || scrollContained ? 'flex min-h-0 flex-col' : 'px-4 pt-4 pb-10 sm:px-6 sm:pt-5 sm:pb-12 md:px-8 md:pt-6 md:pb-16'}`}>
            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={location.pathname}
                className={workspace || scrollContained ? 'flex min-h-0 min-w-0 flex-1 flex-col' : 'min-w-0'}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.1, ease: 'easeOut' }}
              >
                <Outlet />
              </motion.div>
            </AnimatePresence>
          </div>
        </main>

      </div>
    </div>
    </LayoutChromeContext.Provider>
  );
}
